import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronDown, ChevronUp, Plus, Trash } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TenantMonitoringCompanyDto } from '../../Tenants/tenantsApi';
import { removeMonitoringCompany, updateMonitoringCompany } from '../siteApi';
import { getSearchTenantsTenantMonitoring } from '../../Tenants/tenantsApi';
import { useAuthStore } from '@/store/authStore';
import { Input } from '@/components/ui/input';
import { ProjectMonitoringDto } from '../siteApi';

interface MonitoringCompaniesProps {
  siteId: string;
  customerId: string;
  monitoringCompanies: ProjectMonitoringDto[];
}

export function MonitoringCompanies({
  customerId,
  siteId,
  monitoringCompanies,
}: MonitoringCompaniesProps) {
  const auth = useAuthStore();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [addCompanyDialog, setAddCompanyDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<ProjectMonitoringDto | null>(null);
  const [selectedCompanyAdd, setSelectedCompanyAdd] = useState<TenantMonitoringCompanyDto | null>(
    null
  );
  const [accountId, setAccountId] = useState('');
  const queryClient = useQueryClient();

  const { data: availableCompanies, isLoading: loadingCompanies } = useQuery({
    queryKey: ['monitoringCompanies', customerId],
    queryFn: async () => {
      const result = await getSearchTenantsTenantMonitoring(auth.tenant?.tenantId ?? '');
      return result;
    },
    enabled: addCompanyDialog, // Only runs when the dialog is opened
  });

  const { mutate: remove } = useMutation({
    mutationFn: ({
      customerId,
      projectId,
      monitoringCompanyId,
    }: {
      customerId: string;
      projectId: string;
      monitoringCompanyId: string;
    }) => removeMonitoringCompany(customerId, projectId, monitoringCompanyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      setRemoveDialogOpen(false);
    },
    onError: () => {
      // toast.error('Failed to remove monitoring company');
    },
  });

  const { mutate: reorder } = useMutation({
    mutationFn: ({
      customerId,
      projectId,
      monitoringCompanyId,
      data,
    }: {
      customerId: string;
      projectId: string;
      monitoringCompanyId: string;
      data: { order: number };
    }) => updateMonitoringCompany(customerId, projectId, monitoringCompanyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
    onError: () => {
      // toast.error('Failed to reorder monitoring company');
    },
  });

  const { mutate: add } = useMutation({
    mutationFn: async ({
      customerId,
      projectId,
      monitoringCompanyId,
      data,
    }: {
      customerId: string;
      projectId: string;
      monitoringCompanyId: string;
      data: { externalId: string; order: number };
    }) => {
      return updateMonitoringCompany(customerId, projectId, monitoringCompanyId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      setAddCompanyDialog(false);
      setSelectedCompany(null);
      setAccountId('');
    },
    onError: () => {
      // toast.error('Failed to add monitoring company');
    },
  });

  const handleRemove = (company: TenantMonitoringCompanyDto) => {
    console.log('Removing company', company);
    setSelectedCompany(company);
    setRemoveDialogOpen(true);
  };

  const confirmRemove = () => {
    console.log('Confirming remove', selectedCompany);
    if (selectedCompany) {
      remove({
        customerId: customerId,
        projectId: siteId,
        monitoringCompanyId: selectedCompany.monitoringCompanyId!,
      });
    }
  };

  const moveUp = (index: number) => {
    if (index > 0) {
      const company = monitoringCompanies[index];
      const prevCompany = monitoringCompanies[index - 1];

      // Update the current company's order
      reorder({
        customerId: customerId,
        projectId: siteId,
        monitoringCompanyId: company.monitoringCompanyId!,
        data: { order: prevCompany.order! },
      });

      // Update the previous company's order
      reorder({
        customerId: customerId,
        projectId: siteId,
        monitoringCompanyId: prevCompany.monitoringCompanyId!,
        data: { order: company.order! },
      });
    }
  };

  const moveDown = (index: number) => {
    if (index < monitoringCompanies.length - 1) {
      const company = monitoringCompanies[index];
      const nextCompany = monitoringCompanies[index + 1];

      // Update the current company's order
      reorder({
        customerId: customerId,
        projectId: siteId,
        monitoringCompanyId: company.monitoringCompanyId!,
        data: { order: nextCompany.order! },
      });

      // Update the next company's order
      reorder({
        customerId: customerId,
        projectId: siteId,
        monitoringCompanyId: nextCompany.monitoringCompanyId!,
        data: { order: company.order! },
      });
    }
  };

  const handleAdd = () => {
    if (!selectedCompanyAdd) return;

    add({
      customerId,
      projectId: siteId,
      monitoringCompanyId: selectedCompanyAdd.id!,
      data: {
        externalId: accountId,
        order: (monitoringCompanies?.length || 0) + 1,
      },
    });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b p-4">
          <div>
            {' '}
            <h2 className="text-xl font-semibold">Monitoring Companies</h2>
            <p className="text-sm text-muted-foreground">Configure monitoring companies</p>
          </div>
          <Button
            onClick={(e) => {
              e.preventDefault();
              setAddCompanyDialog(true);
            }}
            size="sm"
          >
            Add Company
          </Button>
        </div>
        {monitoringCompanies && monitoringCompanies.length === 0 && (
          <div className="m-4 text-gray-400">No monitoring companies configured</div>
        )}
        <div className="m-4 rounded-md border">
          {monitoringCompanies.map((company, index) => (
            <div
              key={company.monitoringCompanyId}
              className="flex items-center justify-between border-b p-4 last:border-b-0"
            >
              <div>
                <div className="font-semibold">{company.monitoringCompanyName}</div>
                {company.externalId && (
                  <div className="text-sm text-gray-400">Account ID: {company.externalId}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                >
                  <ChevronUp
                    className={`h-4 w-4 ${index === 0 ? 'text-gray-400' : 'text-green-500'}`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveDown(index)}
                  disabled={index === monitoringCompanies.length - 1}
                >
                  <ChevronDown
                    className={`h-4 w-4 ${index === monitoringCompanies.length - 1 ? 'text-gray-400' : 'text-red-500'}`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemove(company);
                  }}
                >
                  <Trash className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Monitoring Company</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {selectedCompany?.monitoringCompanyName}? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmRemove}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addCompanyDialog} onOpenChange={setAddCompanyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Monitoring Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 p-4">
            {loadingCompanies ? (
              <div className="p-4 text-center">Loading companies...</div>
            ) : !availableCompanies?.rows?.length ? (
              <div className="p-4 text-center text-muted-foreground">No companies available</div>
            ) : (
              <div className="space-y-4">
                <div className="max-h-[300px] space-y-2 overflow-y-auto px-1">
                  {availableCompanies.rows
                    .filter(
                      (company) =>
                        !monitoringCompanies.some((mc) => mc.monitoringCompanyId === company.id)
                    )
                    .map((company) => (
                      <div
                        key={company.id!}
                        className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                          selectedCompanyAdd?.id === company.id
                            ? 'border-green-500'
                            : 'border-gray-200 dark:border-gray-800'
                        }`}
                        onClick={() => setSelectedCompanyAdd(company)}
                      >
                        <div className="font-medium">{company.name}</div>
                      </div>
                    ))}
                </div>
                {availableCompanies.rows.length > 0 &&
                  availableCompanies.rows.filter(
                    (company) =>
                      !monitoringCompanies.some((mc) => mc.monitoringCompanyId === company.id)
                  ).length === 0 && (
                    <div className="p-4 text-center text-muted-foreground">
                      All available companies have been added
                    </div>
                  )}
                {selectedCompanyAdd && (
                  <div className="space-y-2 p-2">
                    <label className="text-sm font-medium">Account ID</label>
                    <Input
                      type="text"
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      placeholder="Enter account ID"
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCompanyDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleAdd();
              }}
              disabled={!selectedCompanyAdd}
            >
              Add Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
